/**
 * HeroSection
 *
 * Primary landing hero with theme-aware imagery, responsive typographic scale,
 * and prominent signup/demo CTAs.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingHeader from '@/components/header/landing/LandingHeader';
import { useTheme } from '@/hooks/useDarkMode';
import { kevinMeetingUrl } from '@/data/scheduling/schedulingLinks';

// Function to handle smooth scrolling to contact section
const scrollToContact = (e) => {
  e.preventDefault();
  const contactElement = document.getElementById('contact');
  if (contactElement) {
    contactElement.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  }
};

export function HeroSection() {
  const [isDark, setIsDark] = useState(false);
  const [fontSize, setFontSize] = useState('3.5rem');
  const [descSize, setDescSize] = useState('1.125rem'); // md text size (18px)
  const [footerSize, setFooterSize] = useState('1rem'); // base text size (16px)
  const [buttonSize, setButtonSize] = useState('0.875rem'); // text-sm (14px)
  const [topPadding, setTopPadding] = useState('0');
  const [visible, setVisible] = useState(false);
  
  // Determine which hero images to use based on theme
  const bigHeroImage = isDark ? "/images/hero/big-hero-dark.jpeg" : "/images/hero/big-hero.jpeg";
  const smallHeroImage = isDark ? "/images/hero/small-hero-dark.jpeg" : "/images/hero/small-hero.jpeg";

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // Function to update font size based on window width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // Update top padding based on breakpoints
      if (width >= 1024) { // lg breakpoint
        if (width >= 1500) {
          setTopPadding('24px');
        } else if (width >= 1280) {
          setTopPadding('32px');
        } else if (width >= 1180) {
          setTopPadding('40px');
        } else {
          setTopPadding('24px');
        }
      } else {
        setTopPadding('0');
      }
      
      // Update font sizes based on breakpoints
      // Headline text - more moderate shrinking
      if (width >= 1500) {
        setFontSize('3.3rem');
      } else if (width >= 1400) {
        setFontSize('3.25rem');
      } else if (width >= 1300) {
        setFontSize('3.05rem');
      } else if (width >= 1280) {
        setFontSize('2.95rem');
      } else if (width >= 1250) {
        setFontSize('2.85rem');
      } else if (width >= 1220) {
        setFontSize('2.75rem');
      } else if (width >= 1200) {
        setFontSize('2.65rem');
      } else if (width >= 1180) {
        setFontSize('2.55rem');
      } else if (width >= 1100) {
        setFontSize('2.45rem');
      } else if (width >= 1050) {
        setFontSize('2.35rem');
      } else if (width >= 1024) { // lg breakpoint
        setFontSize('2.25rem');
      } else if (width >= 768) { // md breakpoint
        setFontSize('3.05rem');
      } else if (width >= 640) { // sm breakpoint
        setFontSize('2.55rem');
      } else {
        setFontSize('2.3rem');
      }
      
      // Description text
      if (width >= 1280) {
        setDescSize('1.125rem'); // md (18px)
      } else if (width >= 1180) {
        setDescSize('1rem'); // base (16px)
      } else if (width >= 1100) {
        setDescSize('0.875rem'); // sm (14px)
      } else if (width >= 1024) {
        setDescSize('0.875rem'); // sm (14px)
      } else {
        setDescSize('1rem'); // base (16px)
      }
      
      // Footer text
      if (width >= 1280) {
        setFooterSize('1rem'); // base (16px)
      } else if (width >= 1180) {
        setFooterSize('0.875rem'); // sm (14px)
      } else if (width >= 1024) {
        setFooterSize('0.875rem'); // sm (14px)
      } else {
        setFooterSize('0.875rem'); // sm (14px)
      }
      
      // Button text - keep consistent across most breakpoints
      if (width >= 1180) {
        setButtonSize('0.875rem'); // sm (14px)
      } else if (width >= 1024) {
        setButtonSize('0.85rem'); // slightly smaller
      } else {
        setButtonSize('0.875rem'); // sm (14px)
      }
    };

    // Initial call
    handleResize();
    
    // Set up event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="relative w-full overflow-hidden bg-white dark:bg-gray-900">
      {/* Fading wrapper */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.4s ease-out',
          willChange: 'opacity',
          transform: 'translateZ(0)'
        }}
      >
        {/* Header */}
        <div>
          <LandingHeader />
        </div>

        {/* Main hero content - reordered for mobile */}
        <div className="relative z-10 pb-12 md:pb-32 lg:pb-40">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Mobile-first approach with reordered elements */}
            <div className="flex flex-col lg:flex-row items-center">
              {/* Image displayed at different sizes based on screen width */}
              <div className="w-full lg:hidden mb-6 mt-4 flex justify-center">
                <div className="w-[85%] sm:w-[80%] md:w-[65%] relative">
                  <img 
                    src={bigHeroImage} 
                    alt="Political campaign templates" 
                    className="w-full object-contain rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_25px_rgba(130,100,220,0.15)] dark:border dark:border-purple-500/10"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    style={{}}
                  />
                </div>
              </div>
              
              {/* Left column with text - larger on tablet, scaled for mobile */}
              <div 
                className="w-full lg:w-[45%] mb-12 lg:mb-0 text-center lg:text-left"
                style={{ paddingTop: topPadding }}
              >
                <h1 
                  className="font-bold tracking-tight text-gray-900 dark:text-white leading-tight text-balance"
                  style={{ fontSize }}
                >
                  <span className="block mb-1 sm:mb-2">The <span className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent">Fastest</span> Way To</span>
                  <span className="inline-block text-gray-900 dark:text-white pb-1 sm:pb-2">
                    Scale Political Creative
                  </span>
                </h1>
                <p 
                  className="mt-3 sm:mt-4 md:mt-5 text-pretty max-w-lg mx-auto lg:mx-0 font-medium leading-relaxed sm:leading-8 text-gray-600 dark:text-gray-300"
                  style={{ fontSize: descSize }}
                >
                  Featuring 500+ Political Creative Templates for Campaigns and Causes.
                </p>
                <div className="mt-6 sm:mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 md:gap-6 w-[90%] mx-auto sm:w-auto sm:mx-0">
                  <Link
                    to="/signup"
                    className="w-full sm:w-auto text-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 dark:from-purple-500 dark:to-indigo-500 px-5 py-2.5 font-semibold text-white transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 transform hover:-translate-y-0.5 active:translate-y-0"
                    style={{ fontSize: buttonSize }}
                  >
                    Sign up for free
                  </Link>
                  <a
                    href={kevinMeetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto text-center rounded-md border border-gray-300 dark:border-white/20 bg-white/80 dark:bg-white/10 backdrop-blur-sm px-5 py-2.5 font-semibold text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                    style={{ fontSize: buttonSize }}
                  >
                    Schedule a demo
                  </a>
                </div>
                <p 
                  className="mt-3 sm:mt-4 md:mt-5 text-gray-500 dark:text-gray-400"
                  style={{ fontSize: footerSize }}
                >
                  New templates added every week. Free trial for new customers.
                </p>
              </div>
              
              {/* Desktop images - hidden below 1030px (lg breakpoint), shown from lg up */}
              <div className="hidden lg:block lg:w-[55%] relative h-[400px] lg:h-[500px] xl:h-[600px] lg:ml-auto pt-16 md:pt-16 pb-24 lg:pt-12">
                <div className="relative h-full flex items-center justify-end overflow-visible">
                  {/* Big hero image - using min-width instead of percentage width for consistent sizing */}
                  <div className="absolute z-10 
                    right-[-20%] lg:right-[-40%] xl:right-[-50%] 2xl:right-[-50%] 
                    top-[5%]
                    min-w-[650px] max-[1280px]:min-w-[600px] max-[1180px]:min-w-[550px]
                    max-w-[800px] max-[1280px]:max-w-[750px] max-[1180px]:max-w-[700px]
                    filter drop-shadow-lg dark:drop-shadow-[0_10px_20px_rgba(120,100,220,0.15)]">
                    <img 
                      src={bigHeroImage} 
                      alt="Political campaign templates" 
                      className="w-full object-contain rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_5px_20px_rgba(120,80,220,0.2)] dark:border dark:border-purple-500/10"
                      loading="eager"
                      decoding="async"
                      fetchpriority="high"
                      style={{}}
                    />
                    
                    {/* Small hero image - positioned relative to the big image container */}
                    <div className="absolute z-20 
                      bottom-[-20%] 
                      left-[-5%]
                      w-[40%] max-[1180px]:w-[35%]
                      filter drop-shadow-md dark:drop-shadow-[0_5px_15px_rgba(100,80,220,0.05)]">
                      <img 
                        src={smallHeroImage} 
                        alt="Template example" 
                        className="w-full object-contain rounded-lg shadow-[0_5px_15px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_15px_rgba(100,80,220,0.05)] dark:border-purple-500/5"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        style={{}}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}